/**
 * Video Editor Tab Component
 * 
 * Third tab in the Replay app match detail page.
 * Allows users to create highlight reels from event clips.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { videoTagsService, VideoTag } from '../../../shared/services/videoTagsService';
import { detectVideoSource } from '../../../shared/components/video/utils';
import { VideoEditor, SourceEventClip, EditorClip } from '../../../shared/components/video-editor';
import { generateStorageThumbnailUrl } from '../../../shared/utils/thumbnailUtils';
import { videoHighlightsService, VideoHighlight } from '../../../shared/services/videoHighlightsService';
import { useAuth } from '../../../shared/contexts/AuthContext';
import Button from '../../../shared/components/ui/Button';
import SaveHighlightModal from '../components/SaveHighlightModal';
import Modal from '../../../shared/components/ui/Modal';
import MultiSelectDropdown from '../../../shared/components/ui/MultiSelectDropdown';
import { OverlayConfig, defaultOverlayConfig } from '../../../shared/types/overlayConfig';

interface VideoEditorTabProps {
  matchId: string;
  isActive?: boolean;
  highlightToLoadId?: string | null;
  onHighlightLoaded?: () => void;
  onHighlightSaved?: (highlightId?: string, action?: 'create' | 'update') => void;
  onToast?: (message: string, type: 'success' | 'error' | 'info' | 'warning') => void;
}

/**
 * Converts video tags to source event clips for the editor
 */
function videoTagsToSourceClips(tags: VideoTag[]): SourceEventClip[] {
  return tags
    .filter((tag) => {
      // Only include storage videos (not YouTube)
      if (!tag.video?.video_url) return false;
      const source = detectVideoSource(tag.video.video_url);
      return source === 'cloudflare_r2' || source === 'supabase_storage' || source === 'local';
    })
    .filter((tag) => tag.video_time_seconds !== null)
    .map((tag) => ({
      id: tag.id,
      videoId: tag.video_id,
      videoUrl: tag.video?.video_url || '',
      markerTime: tag.video_time_seconds || 0,
      suggestedStartTime: Math.max(0, (tag.video_time_seconds || 0) - 5),
      suggestedEndTime: (tag.video_time_seconds || 0) + 5,
      label: tag.stat_type?.display_name || 'Event',
      thumbnailUrl: tag.video?.video_url
        ? (generateStorageThumbnailUrl(tag.video.video_url, tag.video_time_seconds || 0) ?? undefined)
        : undefined,
      metadata: {
        statType: tag.stat_type?.display_name,
        statKey: tag.stat_type?.stat_key,
        teamName: tag.team?.name,
        playerName: tag.player?.full_name,
        playerId: tag.player_id ?? tag.player?.id ?? undefined,
        matchMinute: tag.match_minute ?? undefined,
        tags: tag.tags || [],
      },
    }));
}

const VideoEditorTab: React.FC<VideoEditorTabProps> = ({
  matchId,
  isActive = true,
  highlightToLoadId,
  onHighlightLoaded,
  onHighlightSaved,
  onToast,
}) => {
  const [videoTags, setVideoTags] = useState<VideoTag[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const [currentClips, setCurrentClips] = useState<EditorClip[]>([]);
  const [currentHighlight, setCurrentHighlight] = useState<VideoHighlight | null>(null);
  const [loadingHighlight, setLoadingHighlight] = useState(false);
  const [savingHighlight, setSavingHighlight] = useState(false);
  const [newProjectCounter, setNewProjectCounter] = useState(0);
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [pendingHighlightName, setPendingHighlightName] = useState('');
  const [isResetConfirmOpen, setIsResetConfirmOpen] = useState(false);
  const [selectedPlayerIds, setSelectedPlayerIds] = useState<string[]>([]); // Empty = all selected
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>([]); // Empty = all selected
  const [eventFilter, setEventFilter] = useState<'key' | 'all'>('all');
  const [rangeBefore, setRangeBefore] = useState(5);
  const [rangeAfter, setRangeAfter] = useState(5);
  const [currentSegmentEffectsConfig, setCurrentSegmentEffectsConfig] = useState<any>(null);
  const [currentOverlayConfig, setCurrentOverlayConfig] = useState<OverlayConfig>(defaultOverlayConfig);
  const editorKey = currentHighlight?.id || `new-${newProjectCounter}`;
  
  // Track if we're editing an existing highlight
  const isEditingExisting = currentHighlight !== null;
  const originalHighlightId = currentHighlight?.id || null;

  // Playback mode - for video player toggle (not editing mode)
  const [playbackMode, setPlaybackMode] = useState<'segments' | 'video'>('segments');
  
  // Check if base stitched video is available (for enabling Stitched Video toggle button)
  // Only checks for output_url/output_storage_path (base stitched video from stitch_clips)
  const hasStitchedVideo = useMemo(() => {
    if (!currentHighlight) return false;
    const validStatus = ['stitched', 'completed'].includes(currentHighlight.status);
    return validStatus && !!(currentHighlight.output_url || currentHighlight.output_storage_path);
  }, [currentHighlight]);

  // Load video URL for playback toggle
  // For "Stitched Video" mode: ONLY use output_url (base stitched video from stitch_clips)
  // For other uses: Priority: enhanced_url > output_url > constructed from enhanced_storage_path > constructed from output_storage_path
  const videoUrl = useMemo(() => {
    if (!currentHighlight) return null;
    
    // Check if we have any video available
    const hasVideo = currentHighlight.enhanced_storage_path || currentHighlight.output_storage_path;
    const validStatus = ['stitched', 'completed'].includes(currentHighlight.status);
    
    if (!hasVideo || !validStatus) return null;
    
    // When playbackMode is 'video' (Stitched Video toggle), ONLY use base stitched video (output_url)
    // This ensures users see the base video without overlays in the editor
    if (playbackMode === 'video') {
      // Only use base stitched video (output_url)
      if (currentHighlight.output_url) {
        return currentHighlight.output_url;
      }
      if (currentHighlight.output_storage_path) {
        return `https://cdn.capture.kridah.com/${currentHighlight.output_storage_path}`;
      }
      return null;
    }
    
    // For other uses (not in Stitched Video mode), prefer enhanced URL if available
    if (currentHighlight.enhanced_url) {
      return currentHighlight.enhanced_url;
    }
    if (currentHighlight.enhanced_storage_path) {
      return `https://cdn.capture.kridah.com/${currentHighlight.enhanced_storage_path}`;
    }
    
    // Fallback to base video (stitched)
    if (currentHighlight.output_url) {
      return currentHighlight.output_url;
    }
    if (currentHighlight.output_storage_path) {
      return `https://cdn.capture.kridah.com/${currentHighlight.output_storage_path}`;
    }
    
    return null;
  }, [currentHighlight, playbackMode]);

  // Initialize playback mode only when highlight changes (not when videoUrl changes)
  // This prevents resetting the mode when user manually switches between segments/video
  const prevHighlightIdRef = React.useRef<string | null>(null);
  useEffect(() => {
    const currentHighlightId = currentHighlight?.id || null;
    // Only reset playback mode when loading a different highlight
    if (prevHighlightIdRef.current !== currentHighlightId) {
      prevHighlightIdRef.current = currentHighlightId;
      // Reset to segments when loading a new highlight
      setPlaybackMode('segments');
    }
  }, [currentHighlight?.id]);

  // Fetch video tags when tab becomes active
  useEffect(() => {
    if (isActive && matchId && !fetched) {
      const fetchVideoTags = async () => {
        try {
          setLoading(true);
          setError(null);
          const result = await videoTagsService.getVideoTags(matchId);
          if (result.success && result.data) {
            setVideoTags(result.data);
            setError(null); // Clear any previous errors
          } else {
            setVideoTags([]);
            // Only show error if we're not loading a saved highlight (which doesn't need fresh tags)
            if (!highlightToLoadId) {
              setError(result.error || 'Failed to load event clips. You can still edit saved highlights.');
            }
          }
        } catch (err) {
          console.error('Error fetching video tags:', err);
          setVideoTags([]);
          // Only show error if we're not loading a saved highlight
          if (!highlightToLoadId) {
            setError('Failed to load event clips. You can still edit saved highlights.');
          }
        } finally {
          setLoading(false);
          setFetched(true);
        }
      };

      fetchVideoTags();
    }
  }, [isActive, matchId, fetched, highlightToLoadId]);

  // Reset when matchId changes
  useEffect(() => {
    setFetched(false);
    setVideoTags([]);
    setError(null);
  }, [matchId]);

  // Convert video tags to source clips
  const allSourceClips = useMemo(() => videoTagsToSourceClips(videoTags), [videoTags]);

  // Extract unique players from source clips; prefer UUID (playerId) so saved highlights get real player_ids
  const playerOptions = useMemo(() => {
    const playerMap = new Map<string, { id: string; name: string }>();
    allSourceClips.forEach(clip => {
      const playerName = clip.metadata?.playerName;
      if (playerName) {
        const id = clip.metadata?.playerId ?? playerName.toLowerCase().replace(/\s+/g, '_');
        if (!playerMap.has(id)) {
          playerMap.set(id, { id, name: playerName });
        }
      }
    });
    return Array.from(playerMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [allSourceClips]);

  const teamOptions = useMemo(() => {
    const teamMap = new Map<string, { id: string; name: string }>();
    allSourceClips.forEach(clip => {
      const teamName = clip.metadata?.teamName;
      if (teamName) {
        const teamId = teamName.toLowerCase().replace(/\s+/g, '_');
        if (!teamMap.has(teamId)) {
          teamMap.set(teamId, {
            id: teamId,
            name: teamName
          });
        }
      }
    });
    return Array.from(teamMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [allSourceClips]);

  // Key event stat types (matching SourcePanel)
  const KEY_EVENT_STAT_TYPES = [
    'goal_scored', 'shot_inside_box_goal', 'shot_outside_box_goal', 'penalty_scored',
    'penalty_missed', 'penalty_shootout_scored', 'penalty_shootout_missed', 'own_goal',
    'yellow_card', 'red_card', 'substitution', 'kick_off', 'end_1st_half', 'start_2nd_half',
    'full_time', 'start_extra_time_1st_half', 'end_extra_time_1st_half', 'start_extra_time_2nd_half',
    'penalty_shootout', 'save_open_play', 'save_penalty', 'penalty_punch', 'penalty_claim'
  ];

  // Filter source clips based on selected players, teams, and event filter
  const sourceClips = useMemo(() => {
    let filtered = [...allSourceClips];

    // Apply key events filter
    if (eventFilter === 'key') {
      filtered = filtered.filter((clip) => {
        const statKey = clip.metadata?.statKey || '';
        return KEY_EVENT_STAT_TYPES.includes(statKey);
      });
    }

    // Apply player filter (empty array = all selected)
    if (selectedPlayerIds.length > 0) {
      filtered = filtered.filter((clip) => {
        const playerId = clip.metadata?.playerId ?? (clip.metadata?.playerName
          ? clip.metadata.playerName.toLowerCase().replace(/\s+/g, '_')
          : null);
        if (!playerId) return false;
        return selectedPlayerIds.includes(playerId);
      });
    }

    // Apply team filter (empty array = all selected)
    if (selectedTeamIds.length > 0) {
      filtered = filtered.filter((clip) => {
        const teamName = clip.metadata?.teamName;
        if (!teamName) return false;
        const teamId = teamName.toLowerCase().replace(/\s+/g, '_');
        return selectedTeamIds.includes(teamId);
      });
    }

    return filtered;
  }, [allSourceClips, selectedPlayerIds, selectedTeamIds, eventFilter]);

  // Check if any filters are active
  const hasActiveFilters = selectedPlayerIds.length > 0 || selectedTeamIds.length > 0 || eventFilter === 'key';

  // Clear all filters
  const handleClearFilters = () => {
    setSelectedPlayerIds([]);
    setSelectedTeamIds([]);
    setEventFilter('all');
  };

  // Save state function
  const saveEditorState = useCallback(() => {
    // Only save state if we have clips or a highlight (not empty state)
    if ((currentClips.length > 0 || currentHighlight) && !highlightToLoadId) {
      const stateToSave = {
        currentClips,
        currentHighlight: currentHighlight?.id || null,
        currentSegmentEffectsConfig,
        currentOverlayConfig,
        playbackMode,
        matchId,
      };
      try {
        sessionStorage.setItem(`video-editor-state-${matchId}`, JSON.stringify(stateToSave));
      } catch (e) {
        console.warn('Failed to save editor state:', e);
      }
    }
  }, [currentClips, currentHighlight, currentSegmentEffectsConfig, currentOverlayConfig, playbackMode, matchId, highlightToLoadId]);

  // Persist editor state to prevent loss on tab switch/alt-tab
  useEffect(() => {
    saveEditorState();
  }, [saveEditorState]);

  // Save state when window loses focus (alt-tab, switch tabs, etc.)
  useEffect(() => {
    const handleBlur = () => {
      saveEditorState();
    };
    const handleVisibilityChange = () => {
      if (document.hidden) {
        saveEditorState();
      }
    };
    window.addEventListener('blur', handleBlur);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('blur', handleBlur);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [saveEditorState]);

  // Restore editor state when tab becomes active (only if state was lost)
  useEffect(() => {
    if (isActive && !highlightToLoadId && currentClips.length === 0 && !currentHighlight) {
      try {
        const savedState = sessionStorage.getItem(`video-editor-state-${matchId}`);
        if (savedState) {
          const state = JSON.parse(savedState);
          // Only restore if it's for the same match and we have clips to restore
          if (state.matchId === matchId && state.currentClips && state.currentClips.length > 0) {
            setCurrentClips(state.currentClips);
            setCurrentSegmentEffectsConfig(state.currentSegmentEffectsConfig || null);
            setCurrentOverlayConfig(state.currentOverlayConfig || defaultOverlayConfig);
            setPlaybackMode(state.playbackMode || 'segments');
            // Note: currentHighlight is not restored from sessionStorage as it requires a full fetch
            // User would need to reload the highlight manually if needed
          }
        }
      } catch (e) {
        console.warn('Failed to restore editor state:', e);
      }
    }
  }, [isActive, highlightToLoadId, matchId, currentClips.length, currentHighlight]); // Only run when tab becomes active

  // Load highlight if requested
  useEffect(() => {
    const loadHighlight = async (highlightId: string) => {
      setLoadingHighlight(true);
      const result = await videoHighlightsService.getHighlightById(highlightId);
      if (result.success && result.data) {
        const highlight = result.data;
        setCurrentHighlight(highlight);
        setCurrentClips(highlight.segments_data || []);
        setSelectedPlayerIds(Array.isArray(highlight.player_ids) ? highlight.player_ids : []);
        // Load configs
        setCurrentSegmentEffectsConfig(highlight.segment_effects_config || null);
        // Parse overlay config or use default
        const overlayConfig = highlight.overlay_config 
          ? (typeof highlight.overlay_config === 'string' 
              ? JSON.parse(highlight.overlay_config) 
              : highlight.overlay_config)
          : defaultOverlayConfig;
        setCurrentOverlayConfig(overlayConfig);
        // Clear saved state since we're loading a specific highlight
        try {
          sessionStorage.removeItem(`video-editor-state-${matchId}`);
        } catch (e) {
          // Ignore
        }
      } else {
        onToast?.(result.error || 'Failed to load highlight', 'error');
        setCurrentHighlight(null);
        setCurrentClips([]);
        setCurrentSegmentEffectsConfig(null);
        setCurrentOverlayConfig(defaultOverlayConfig);
      }
      setLoadingHighlight(false);
      onHighlightLoaded?.();
    };

    if (highlightToLoadId) {
      loadHighlight(highlightToLoadId);
    }
    // Don't reset state when highlightToLoadId is cleared - let state persist
    // This prevents reset when user switches tabs or alt-tabs
  }, [highlightToLoadId, onHighlightLoaded, onToast, matchId]);

  const handleResetEditor = () => {
    setIsResetConfirmOpen(true);
  };

  const confirmResetEditor = () => {
    setCurrentHighlight(null);
    setCurrentClips([]);
    setCurrentSegmentEffectsConfig(null);
    setCurrentOverlayConfig(defaultOverlayConfig);
    setNewProjectCounter((count) => count + 1);
    setIsResetConfirmOpen(false);
    onHighlightLoaded?.();
  };

  // Only persist player IDs that are UUIDs (from player filter); empty filter = do not associate players
  const playerIdsFromFilter = useMemo(() => {
    if (selectedPlayerIds.length === 0) return null;
    const uuids = selectedPlayerIds.filter((id) => id.length === 36 && /^[0-9a-f-]+$/i.test(id));
    return uuids.length > 0 ? uuids : null;
  }, [selectedPlayerIds]);

  const handleSaveHighlight = async (name: string, action: 'update' | 'create') => {
    if (!user?.id) {
      onToast?.('Please sign in to save highlights.', 'error');
      return;
    }
    
    // Require clips for saving
    if (currentClips.length === 0) {
      onToast?.('Add at least one clip before saving.', 'error');
      return;
    }

    setSavingHighlight(true);
    if (action === 'update' && originalHighlightId) {
      const updatePayload: any = {
        name,
        updatedBy: user.id,
        segments: currentClips,
        player_ids: playerIdsFromFilter,
      };
      
      // Always include configs if they exist
      if (currentSegmentEffectsConfig) {
        updatePayload.segmentEffectsConfig = currentSegmentEffectsConfig;
      }
      if (currentOverlayConfig) {
        // Remove logoData from watermark config before saving (worker uses logoType only)
        const cleanedOverlayConfig = { ...currentOverlayConfig };
        if (cleanedOverlayConfig.watermark && cleanedOverlayConfig.watermark.logoData) {
          cleanedOverlayConfig.watermark = { ...cleanedOverlayConfig.watermark };
          delete cleanedOverlayConfig.watermark.logoData;
        }
        updatePayload.overlayConfig = cleanedOverlayConfig;
      }
      
      const result = await videoHighlightsService.updateHighlight(originalHighlightId, updatePayload);
      setSavingHighlight(false);
      if (!result.success || !result.data) {
        onToast?.(result.error || 'Failed to update highlight.', 'error');
        return;
      }
      setCurrentHighlight(result.data);
      onToast?.('Highlight updated successfully.', 'success');
      onHighlightSaved?.(result.data.id, 'update');
      setIsSaveModalOpen(false);
    } else {
      const result = await videoHighlightsService.createHighlight({
        matchId,
        name,
        segments: currentClips,
        createdBy: user.id,
        ...(playerIdsFromFilter !== null && { player_ids: playerIdsFromFilter }),
      });
      
      setSavingHighlight(false);
      if (!result.success || !result.data) {
        onToast?.(result.error || 'Failed to save highlight.', 'error');
        return;
      }
      
      // After creating, update with configs if they exist
      if (currentSegmentEffectsConfig || currentOverlayConfig) {
        // Remove logoData from watermark config before saving (worker uses logoType only)
        let cleanedOverlayConfig = currentOverlayConfig;
        if (cleanedOverlayConfig?.watermark?.logoData) {
          cleanedOverlayConfig = { ...cleanedOverlayConfig };
          if (cleanedOverlayConfig.watermark) {
            cleanedOverlayConfig.watermark = { ...cleanedOverlayConfig.watermark };
            delete cleanedOverlayConfig.watermark.logoData;
          }
        }
        
        const updateResult = await videoHighlightsService.updateHighlight(result.data.id, {
          segmentEffectsConfig: currentSegmentEffectsConfig,
          overlayConfig: cleanedOverlayConfig,
          updatedBy: user.id,
        });
        if (updateResult.success && updateResult.data) {
          setCurrentHighlight(updateResult.data);
        } else {
          setCurrentHighlight(result.data);
        }
      } else {
      setCurrentHighlight(result.data);
      }
      
      onToast?.('Highlight saved successfully.', 'success');
      onHighlightSaved?.(result.data.id, 'create');
      setIsSaveModalOpen(false);
    }
  };

  const openSaveModal = () => {
    if (!user?.id) {
      onToast?.('Please sign in to save highlights.', 'error');
      return;
    }
    if (currentClips.length === 0) {
      onToast?.('Add at least one clip before saving.', 'error');
      return;
    }
    const defaultName = currentHighlight?.name || `Highlight ${new Date().toLocaleString()}`;
    setPendingHighlightName(defaultName);
    setIsSaveModalOpen(true);
  };

  const handleExport = (exportData: any) => {
    console.log('Export requested:', exportData);
    alert('Export feature coming soon! This will create a highlight reel from your selected clips.');
  };

  // Loading state - only show if we're not loading a saved highlight
  if (loading && !highlightToLoadId && !currentHighlight) {
    return (
      <div className="flex items-center justify-center h-[600px] bg-white rounded-lg border border-gray-200">
        <div className="flex items-center space-x-2">
          <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          <span className="text-gray-500">Loading editor...</span>
        </div>
      </div>
    );
  }

  // Error state - only block if we don't have a saved highlight to work with
  if (error && !currentHighlight && !highlightToLoadId) {
    return (
      <div className="flex items-center justify-center h-[600px] bg-white rounded-lg border border-gray-200">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-50 rounded-lg flex items-center justify-center">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  const renderNoSourceState = () => (
    <div className="flex items-center justify-center h-[600px] bg-white rounded-lg border border-gray-200">
      <div className="text-center">
        <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-lg flex items-center justify-center">
          <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        </div>
        <p className="text-gray-600 text-sm mb-2">No storage video clips available</p>
        <p className="text-gray-400 text-xs">
          Upload match videos to R2/Supabase storage to use the editor.
          <br />
          YouTube videos cannot be edited.
        </p>
      </div>
    </div>
  );

  const toolbarActions = (
    <div className="flex items-center gap-2">
      <Button variant="ghost" size="sm" onClick={handleResetEditor} disabled={savingHighlight || loadingHighlight}>
        Reset Editor
      </Button>
      <Button
        variant="primary"
        size="sm"
        onClick={openSaveModal}
        disabled={currentClips.length === 0 || savingHighlight || loadingHighlight}
      >
        Save
      </Button>
    </div>
  );

  return (
    <div className="flex flex-col gap-4" style={{ minHeight: '500px' }}>
      {error && (currentHighlight || highlightToLoadId) && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-xs text-yellow-800">
          <div className="flex items-start gap-2">
            <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <div>
              <p className="font-medium">Could not load event clips from source</p>
              <p className="text-yellow-700 mt-1">{error}</p>
              <p className="text-yellow-600 mt-1">You can still edit your saved highlight below.</p>
            </div>
          </div>
        </div>
      )}
      {currentHighlight && (
        <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm text-xs text-gray-500">
          Editing highlight:{' '}
          <span className="font-semibold text-gray-800">{currentHighlight.name}</span> • Last updated{' '}
          {new Date(currentHighlight.updated_at).toLocaleString()} • Status{' '}
          <span className="font-medium capitalize">{currentHighlight.status}</span>
        </div>
      )}

      {/* Filters - Outside editor for better space */}
      {allSourceClips.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 overflow-visible">
          {/* Filter Header */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <div className="w-6 h-6 bg-blue-50 rounded flex items-center justify-center">
                <svg className="w-3.5 h-3.5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
              </div>
              <h3 className="text-sm font-medium text-gray-900">Filters</h3>
            </div>
            {hasActiveFilters && (
              <button
                onClick={handleClearFilters}
                className="text-xs text-blue-600 hover:text-blue-800 font-medium px-2 py-1 rounded hover:bg-blue-50 transition-colors"
              >
                Clear All
              </button>
            )}
          </div>

          {/* Filter Controls */}
          <div className="flex items-end justify-between gap-4 flex-wrap overflow-visible">
            {/* Left Side: Events and Range */}
            <div className="flex items-end gap-3 flex-wrap overflow-visible">
              {/* Event Filter Toggle */}
              <div className="overflow-visible">
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Events
                </label>
                <div className="flex items-center space-x-1 h-[38px]">
                  <button
                    onClick={() => setEventFilter('key')}
                    className={`px-3 py-2 rounded text-xs font-medium transition-colors h-full flex items-center ${
                      eventFilter === 'key'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                    }`}
                  >
                    Key
                  </button>
                  <button
                    onClick={() => setEventFilter('all')}
                    className={`px-3 py-2 rounded text-xs font-medium transition-colors h-full flex items-center ${
                      eventFilter === 'all'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                    }`}
                  >
                    All
                  </button>
                </div>
              </div>

              {/* Range Selector */}
              <div className="overflow-visible">
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Range
                </label>
                <div className="flex items-center space-x-1 h-[38px]">
                  <input
                    type="number"
                    min="0"
                    max="30"
                    value={rangeBefore}
                    onChange={(e) => setRangeBefore(Math.max(0, parseInt(e.target.value) || 0))}
                    className="w-12 px-2 text-xs text-center bg-white border border-gray-300 rounded text-gray-700 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 h-full"
                  />
                  <span className="text-xs text-gray-400">s /</span>
                  <input
                    type="number"
                    min="0"
                    max="30"
                    value={rangeAfter}
                    onChange={(e) => setRangeAfter(Math.max(0, parseInt(e.target.value) || 0))}
                    className="w-12 px-2 text-xs text-center bg-white border border-gray-300 rounded text-gray-700 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 h-full"
                  />
                  <span className="text-xs text-gray-400">s</span>
                </div>
              </div>
            </div>

            {/* Right Side: Players and Teams */}
            <div className="flex items-end gap-3 flex-wrap overflow-visible">
              {/* Players Filter */}
              {playerOptions.length > 0 && (
                <div className="min-w-[200px] overflow-visible relative z-10">
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Players
                  </label>
                  <MultiSelectDropdown
                    selectedIds={selectedPlayerIds}
                    onChange={setSelectedPlayerIds}
                    options={playerOptions}
                    placeholder="All players"
                    showSearch={true}
                    className="[&>div>div]:min-h-[38px] [&>div>div]:h-[38px] [&>div>div]:text-xs"
                  />
                </div>
              )}

              {/* Teams Filter */}
              {teamOptions.length > 0 && (
                <div className="min-w-[200px] overflow-visible relative z-10">
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Teams
                  </label>
                  <MultiSelectDropdown
                    selectedIds={selectedTeamIds}
                    onChange={setSelectedTeamIds}
                    options={teamOptions}
                    placeholder="All teams"
                    showSearch={true}
                    className="[&>div>div]:min-h-[38px] [&>div>div]:h-[38px] [&>div>div]:text-xs"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {loadingHighlight ? (
        <div className="flex items-center justify-center h-[400px] bg-white border border-gray-200 rounded-lg">
          <p className="text-sm text-gray-500">Loading highlight…</p>
        </div>
      ) : sourceClips.length === 0 ? (
        renderNoSourceState()
      ) : (
        <div style={{ height: 'calc(100vh - 220px)', minHeight: '500px' }}>
          <VideoEditor
            key={editorKey}
            matchId={matchId}
            sources={{
              eventClips: sourceClips,
            }}
            onExport={handleExport}
            onClipsChange={setCurrentClips}
            toolbarRightContent={toolbarActions}
            initialClips={currentHighlight?.segments_data || []}
            className="h-full"
            rangeBefore={rangeBefore}
            rangeAfter={rangeAfter}
            overlayConfig={currentOverlayConfig}
            onOverlayConfigChange={setCurrentOverlayConfig}
            segmentEffectsConfig={currentSegmentEffectsConfig}
            onSegmentEffectsConfigChange={setCurrentSegmentEffectsConfig}
            videoUrl={videoUrl}
            hasStitchedVideo={hasStitchedVideo}
            playbackMode={playbackMode}
            onPlaybackModeChange={(mode) => {
              if (mode === 'video' && !hasStitchedVideo) {
                // Can't switch to video if no stitched video exists
                return;
              }
              setPlaybackMode(mode);
            }}
          />
        </div>
      )}
      <SaveHighlightModal
        isOpen={isSaveModalOpen}
        initialName={pendingHighlightName}
        isSaving={savingHighlight}
        isEditingExisting={isEditingExisting}
        highlightStatus={currentHighlight?.status}
        highlight={currentHighlight || undefined}
        currentClips={currentClips}
        currentSegmentEffectsConfig={currentSegmentEffectsConfig}
        currentOverlayConfig={currentOverlayConfig}
        onClose={() => {
          if (!savingHighlight) {
            setIsSaveModalOpen(false);
          }
        }}
        onSave={handleSaveHighlight}
      />
      
      {/* Reset Confirmation Modal */}
      {isResetConfirmOpen && (
        <Modal
          isOpen={isResetConfirmOpen}
          onClose={() => setIsResetConfirmOpen(false)}
          title="Reset Editor"
        >
          <div className="p-6 space-y-4">
            <p className="text-sm text-gray-700">
              Are you sure you want to reset the editor? This will clear all clips from the timeline.
              {isEditingExisting && (
                <span className="block mt-2 text-gray-600">
                  Your saved highlight will not be affected, but any unsaved changes will be lost.
                </span>
              )}
            </p>
            <div className="flex justify-end space-x-3">
              <Button variant="ghost" onClick={() => setIsResetConfirmOpen(false)}>
                Cancel
              </Button>
              <Button variant="primary" onClick={confirmResetEditor}>
                Reset
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default VideoEditorTab;

